import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ip_back } from '../config';
import { decodeJwtPayload } from '../utils/inviteClubId';
import './ConfirmEmail.css';

const apiBase = () => ip_back.replace(/\/$/, '');

function looksLikeJwt(token) {
  return typeof token === 'string' && token.split('.').length === 3;
}

async function fetchUserByEmail(email) {
  const response = await fetch(`${apiBase()}/api/user/getUserByEmail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) return null;
  const data = await response.json();
  return data.status === 'success' ? data.data : null;
}

function isProfileComplete(user) {
  return user.isProfileCompleted === true ||
    (user.main_purpose_id && user.style_id);
}

function userHasAdminRole(user) {
  return !!(user.tbl_admin?.length) || user.hasAdminRole === true || user.type_user_id === 2;
}

function userHasPlayerRole(user) {
  return !!(user.tbl_player?.length) || user.hasPlayerRole === true || user.type_user_id === 4;
}

/**
 * Mid-registration (account exists, profile incomplete) is NOT dual role.
 */
function classifyConfirmationScenario(user, payload) {
  const wantsAdmin = !!(payload.clubData || payload.isClubAdmin);

  if (!user) {
    return { kind: 'new_registration' };
  }

  if (isProfileComplete(user)) {
    return { kind: 'already_registered' };
  }

  const hasAdmin = userHasAdminRole(user);
  const hasPlayer = userHasPlayerRole(user);

  if (hasPlayer && !wantsAdmin) {
    return { kind: 'continue_registration' };
  }

  if (wantsAdmin && hasPlayer && !hasAdmin) {
    return { kind: 'dual_role', newRole: 'admin' };
  }

  if (!wantsAdmin && hasAdmin && !hasPlayer) {
    return { kind: 'dual_role', newRole: 'player' };
  }

  if (wantsAdmin && !hasAdmin) {
    return { kind: 'continue_registration' };
  }

  return { kind: 'continue_registration' };
}

function syncLocalUserVerified(official = null) {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return;
    const u = JSON.parse(raw);
    u.emailVerification = true;
    if (official?.fltt_classement) {
      u.fltt_classement = official.fltt_classement;
      u.custom_level = official.fltt_classement;
      u.player_level = {
        ...(typeof u.player_level === 'object' && u.player_level ? u.player_level : {}),
        level_id: official.level_id || u.player_level?.level_id || null,
        level_name: official.fltt_classement,
        level_description: official.fltt_classement,
      };
    }
    if (official?.fltt_ranking_points != null) {
      u.fltt_ranking_points = official.fltt_ranking_points;
    }
    localStorage.setItem('user', JSON.stringify(u));
  } catch {
    /* ignore */
  }
}

const ConfirmEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [redirectTarget, setRedirectTarget] = useState('password-registration');

  const processPostRegistrationVerify = useCallback(async (token) => {
    try {
      const response = await fetch(
        `${apiBase()}/api/user/verify-email/${encodeURIComponent(token)}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status !== 'success') {
        setStatus('error');
        setMessage(data.message || 'Invalid or expired confirmation link.');
        return;
      }
      syncLocalUserVerified(data.data || null);
      try {
        const token = localStorage.getItem('token');
        const userId = data.data?.user_id;
        if (token && userId) {
          const fresh = await fetch(`${apiBase()}/api/user/getProfile`, {
            headers: {
              Authorization: token,
              'user-id': userId,
              'Content-Type': 'application/json',
            },
          });
          const freshJson = await fresh.json().catch(() => ({}));
          if (fresh.ok && freshJson?.status === 'success' && freshJson.data) {
            const merged = {
              ...freshJson.data,
              user_token: token,
              emailVerification: true,
            };
            localStorage.setItem('user', JSON.stringify(merged));
          }
        }
      } catch {
        /* keep the local merge from the verify response */
      }
      setRedirectTarget('home');
      setMessage(
        'Email confirmed! You can now participate in training sessions.'
      );
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setMessage('Could not confirm your email. Please try again or request a new link from Home.');
    }
  }, []);

  const processConfirmation = useCallback(async (token, mode) => {
    // Digiping / post-signup opaque token
    if (mode === 'verify' || !looksLikeJwt(token)) {
      await processPostRegistrationVerify(token);
      return;
    }

    localStorage.setItem('emailConfirmationToken', token);

    const payload = decodeJwtPayload(token) || {};
    const userEmail = payload.email || '';
    if (userEmail) {
      localStorage.setItem('pendingEmail', userEmail);
    }

    const isClubAdmin = !!(payload.clubData || payload.isClubAdmin);
    const user = userEmail ? await fetchUserByEmail(userEmail) : null;
    const scenario = classifyConfirmationScenario(user, payload);

    if (scenario.kind === 'already_registered') {
      // Completed profile clicking an old JWT link — still mark verified if possible
      setRedirectTarget('login');
      setMessage('This email is already registered with a completed profile. Please log in.');
      setStatus('success');
      return;
    }

    if (scenario.kind === 'dual_role') {
      try {
        const confirmResponse = await fetch(`${apiBase()}/api/user/confirm-dual-role-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (confirmResponse.ok) {
          setRedirectTarget('login');
          setMessage('Email confirmed successfully! Your new role has been added. You can now log in with your existing password.');
          setStatus('success');
          return;
        }
        console.log('Dual role confirm failed:', await confirmResponse.text());
      } catch (error) {
        console.log('Error confirming dual role:', error);
      }
    }

    if (scenario.kind === 'continue_registration') {
      setMessage('Email confirmed! Continue registration to finish your player profile.');
    } else {
      setMessage('Email confirmed successfully! You can now create your password.');
    }

    if (isClubAdmin) {
      // Prefetch DigiPing answers for mobile→desktop (no localStorage on this browser)
      try {
        const pendingRes = await fetch(
          `${apiBase()}/api/user/club-admin-pending?token=${encodeURIComponent(token)}`
        );
        const pendingJson = await pendingRes.json().catch(() => ({}));
        if (pendingRes.ok && pendingJson?.status === 'success' && pendingJson.data?.payload) {
          const existingRaw = localStorage.getItem('profilingFormData');
          let existing = null;
          try {
            existing = existingRaw ? JSON.parse(existingRaw) : null;
          } catch {
            existing = null;
          }
          localStorage.setItem(
            'profilingFormData',
            JSON.stringify({ ...(existing || {}), ...pendingJson.data.payload })
          );
        }
      } catch {
        /* ClubAdminProfiling will retry */
      }
      setRedirectTarget('club-admin-profiling');
    } else {
      setRedirectTarget('password-registration');
    }
    setStatus('success');
  }, [processPostRegistrationVerify]);

  useEffect(() => {
    const token = searchParams.get('token');
    const mode = searchParams.get('mode');
    if (!token) {
      setStatus('error');
      setMessage('Invalid confirmation link. No token provided.');
      return;
    }
    processConfirmation(token, mode);
  }, [searchParams, processConfirmation]);

  const handleContinue = useCallback(() => {
    if (redirectTarget === 'login') {
      navigate('/login', {
        state: { email: localStorage.getItem('pendingEmail') || undefined },
      });
    } else if (redirectTarget === 'club-admin-profiling') {
      navigate('/club-admin-profiling', { state: { emailConfirmed: true } });
    } else if (redirectTarget === 'home') {
      const hasUser = !!localStorage.getItem('user');
      navigate(hasUser ? '/home' : '/login');
    } else {
      navigate('/password-registration');
    }
  }, [navigate, redirectTarget]);

  useEffect(() => {
    if (status !== 'success') return undefined;
    const timer = setTimeout(handleContinue, 3000);
    return () => clearTimeout(timer);
  }, [status, handleContinue]);

  const continueLabel =
    redirectTarget === 'login'
      ? 'Go to Login'
      : redirectTarget === 'home'
        ? 'Go to Home'
        : 'Continue Registration';

  return (
    <div className="confirm-email-container">
      <div className="confirm-email-card">
        <div className="confirm-email-header">
          <h1>Email Confirmation</h1>
        </div>
        
        <div className="confirm-email-content">
          {status === 'loading' && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Confirming your email...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="success-state">
              <div className="success-icon">✅</div>
              <h2>Email Confirmed!</h2>
              <p>{message}</p>
              <button onClick={handleContinue} className="continue-button">
                {continueLabel}
              </button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="error-state">
              <div className="error-icon">❌</div>
              <h2>Confirmation Failed</h2>
              <p>{message}</p>
              <button onClick={() => navigate('/')} className="home-button">
                Go to Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;
